import { type ReactElement, useState, useCallback, type ReactNode } from 'react'
import { Loader } from '@mantine/core'
import { type TinyUser } from '@terros/common'
import { TButton } from 'app/components/buttons'
import { PrefetchedUserInfo } from 'app/components/entityInfo'
import { TListItem } from 'app/components/terros'
import { type PickUserSearchProps } from './PickUserSearch'

type UserItemProps = Pick<PickUserSearchProps, 'onSelect' | 'pickedUserIds' | 'disabledUserIds' | 'onRemove'> & {
  user: TinyUser
  canRemove?: boolean
}

export const UserItem = ({
  user,
  onSelect,
  pickedUserIds,
  disabledUserIds,
  canRemove,
  onRemove,
}: UserItemProps): ReactElement => {
  const itemSelected = pickedUserIds?.includes(user.userId)
  const [isLoading, setIsLoading] = useState(false)

  const handleRemove = useCallback(async () => {
    if (!canRemove) return
    try {
      setIsLoading(true)
      await onRemove?.(user.userId)
    } finally {
      setIsLoading(false)
    }
  }, [canRemove, onRemove, user.userId])

  const handleSelect = useCallback(async () => {
    if (itemSelected) return handleRemove()
    try {
      setIsLoading(true)
      await onSelect(user)
    } finally {
      setIsLoading(false)
    }
  }, [onSelect, user, itemSelected, handleRemove])

  const disabled = isLoading || (itemSelected && !canRemove) || disabledUserIds?.includes(user.userId)

  return (
    <TListItem
      key={user.userId}
      buttonProps={{ disabled, variant: itemSelected ? 'light' : 'subtle' }}
      rightSection={
        <UserItemRightSection canRemove={canRemove} isLoading={isLoading} itemSelected={Boolean(itemSelected)} />
      }
      onClick={handleSelect}
    >
      <PrefetchedUserInfo linked={false} size='md' user={user} />
    </TListItem>
  )
}

type UserItemRightSectionProps = {
  itemSelected: boolean
  isLoading: boolean
  canRemove?: boolean
}

const UserItemRightSection = ({ itemSelected, isLoading, canRemove }: UserItemRightSectionProps): ReactNode => {
  if (isLoading) return <Loader className='self-center' />
  if (!itemSelected) return undefined

  return <TButton buttonType='icon-full' icon='check' />
}
